import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';

import { NgClass } from '@angular/common';
import { Status } from '@df/core/status';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { BehaviorSubject, map } from 'rxjs';
import { BasketQtyComponent } from '../../../basket/qty/basket-qty.component';
import { LocalCustomerModel } from '../../../customer/customer.model.decorator';
import { UserNotificationsQtyBulletDirective } from '../../user-notifications/user-notifications-qty-bullet.directive';

export enum EPwaMenuActiveState {
    home,
    community,
    account,
    basket,
    shop
}

@Component({
    selector: 'pwa-bottom-bar',
    templateUrl: './pwa-bottom-bar.html',
    styleUrls: ['./pwa-bottom-bar.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ICmsClientModule,
        ElementHeightDirective,
        RouterLink,
        TranslatePipe,
        BasketQtyComponent,
        UserNotificationsQtyBulletDirective,
        NgClass
    ]
})
export class PwaBottomBarComponent extends UiComponent {
    private router = inject(Router);
    private meta = inject(Meta);
    private customerModel = inject(LocalCustomerModel);
    private route = inject(ActivatedRoute);
    private app = inject(App);
    protected EPwaMenuActiveState = EPwaMenuActiveState;
    protected communityStatus = new Status();

    @Input()
    offCanvasState!: BehaviorSubject<boolean>;

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(
            this.meta.subscribe(() => {
                this.detectChanges();
            })
        );

        this.subscriptions.add(
            this.route.queryParamMap.pipe(map(queryParams => queryParams.get('from'))).subscribe(fromParam => {
                this.detectChanges();
            })
        );
    }

    protected openOffCanvas(): void {
        this.offCanvasState.next(true);
    }

    protected isCurrentPage(link: EPwaMenuActiveState): boolean {
        switch (link) {
            case EPwaMenuActiveState.home:
                return this.router.url.split('?')[0] === '/';
            case EPwaMenuActiveState.community:
                return (
                    this.router.url === '/community' ||
                    (this.router.url.includes('/sign-in-access') && this.route.snapshot.queryParamMap.get('from') === 'community')
                );
            case EPwaMenuActiveState.account:
                return (
                    this.router.url.includes('/account') ||
                    this.router.url.includes('/login') ||
                    (this.router.url.includes('/sign-in-access') && this.route.snapshot.queryParamMap.get('from') === 'account')
                );
            case EPwaMenuActiveState.basket:
                return this.router.url.includes('/basket') || this.router.url.includes('/checkout');
            case EPwaMenuActiveState.shop:
                return (
                    !this.isCurrentPage(EPwaMenuActiveState.home) &&
                    !this.isCurrentPage(EPwaMenuActiveState.basket) &&
                    !this.isCurrentPage(EPwaMenuActiveState.community) &&
                    !this.isCurrentPage(EPwaMenuActiveState.account)
                );
        }
    }

    protected get canShowCommunity(): boolean {
        return !this.customerModel.isLoggedIn || !!this.customerModel.loyaltyId;
    }

    protected async openCommunity(): Promise<void> {
        await this.customerModel.load();
        if (!this.customerModel.isLoggedIn) {
            await this.router.navigateByUrl('/sign-in-access?from=community');
            return;
        }
        this.communityStatus.busy = true;
        this.detectChanges();
        const siteId = (this.app.dfConfig?.ms?.siteId as string) || '';
        window.open(`https://insiders.lemieux.com/?website=${encodeURIComponent(siteId)}&isapp=1`, '_blank');
        this.communityStatus.busy = false;
        this.detectChanges();
    }
}
